import React, { useState, useEffect } from 'react';
import { TextField, Button, Typography, Box, FormHelperText } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useImmer } from 'use-immer';
import axios from 'axios';
import PropTypes from 'prop-types';

const KratosFlowUIForm = ({ 
    flow,           
    onFlowUpdate,   
    onSuccess,
    onError,
    ignoreUIGroups = [],
    hideGroupTitles = [],
    hideAllGroupTitles = false,
    messages = {},
    ...props 
}) => {
    console.log('[KratosFlowUIForm] Received flow:', flow);
    console.log('[KratosFlowUIForm] Flow state:', flow?.state);
    console.log('[KratosFlowUIForm] UI nodes:', flow?.ui?.nodes);

    const [currentFlow, setCurrentFlow] = useState(flow);
    const [formData, updateFormData] = useImmer({});

    // Initialize form data when flow changes
    useEffect(() => {
        console.log('=== FORM DATA INITIALIZATION ===');
        console.log('currentFlow changed:', currentFlow?.id);
        console.log('currentFlow state:', currentFlow?.state);
        console.log('currentFlow nodes:', currentFlow?.ui?.nodes?.length);
        
        if (currentFlow?.ui?.nodes) {
            updateFormData(draft => {
                // Clear existing data
                Object.keys(draft).forEach(key => delete draft[key]);
                
                // Set initial values from flow nodes (excluding submit buttons)
                currentFlow.ui.nodes
                    .filter(node => node.attributes.type !== 'submit')
                    .forEach(node => {
                        if (node.attributes?.name && node.attributes?.value !== undefined) {
                            console.log(`Setting initial value for ${node.attributes.name}:`, node.attributes.value);
                            draft[node.attributes.name] = node.attributes.value;
                        }
                    });
            });
        }
        console.log('=================================');
    }, [currentFlow, updateFormData]);

    // Handle input changes
    const handleInputChange = (name, value) => {
        updateFormData(draft => {
            draft[name] = value;
        });
    };

    // Apply flow-state-specific form submission overrides
    const applyFlowStateOverrides = (submissionData, submitterName, submitterValue) => {
        const flowState = currentFlow?.state;
        
        switch (flowState) {
            case 'sent_email': {
                // Verification flow: If submitting with empty/missing code via method button,
                // convert empty code to single space to trigger validation error instead of flow change
                const codeValue = submissionData.code;
                if ((!codeValue || codeValue.trim() === '') && submitterName === 'method') {
                    submissionData.code = ' '; // Single space triggers "invalid code" vs flow state change
                }
                break;
            }
                
            // Add more flow state overrides here as needed:
            // case 'awaiting_otp':
            // case 'password_reset':
            // etc.
            
            default:
                // No special handling for other flow states
                break;
        }
    };

    // Handle form submission
    const handleFormSubmit = (event, submitterName, submitterValue) => {
        event.preventDefault();
        
        // Create submission data starting fresh
        const submissionData = {};
        
        // Add all non-submit form values (including empty values)
        currentFlow.ui.nodes
            .filter(node => node.attributes.type !== 'submit')
            .forEach(node => {
                const name = node.attributes.name;
                if (name) {
                    // Submit the form value if it exists, otherwise use the node's default value (including empty string)
                    submissionData[name] = formData[name] !== undefined ? formData[name] : (node.attributes.value || '');
                }
            });
        
        // Apply flow-state-specific form handling overrides
        applyFlowStateOverrides(submissionData, submitterName, submitterValue);
        
        // Add the clicked submit button's value
        if (submitterName && submitterValue !== undefined) {
            submissionData[submitterName] = submitterValue;
        }

        console.log('=== FORM SUBMISSION DEBUG ===');
        console.log('Flow action URL:', currentFlow?.ui?.action);
        console.log('Submitter name:', submitterName);
        console.log('Submitter value:', submitterValue);
        console.log('Original form data:', formData);
        console.log('Final submission data:', submissionData);
        console.log('Will be sent as form-encoded data');
        console.log('===============================');
        mutation.mutate(submissionData);
    };

    // Move all hooks to the top
    const mutation = useMutation({
        mutationFn: (data) => {
            console.log('KratosFlowUIForm mutation', currentFlow?.ui?.action);
            
            // Convert data to URLSearchParams for form encoding
            const formData = new URLSearchParams();
            Object.entries(data).forEach(([key, value]) => {
                formData.append(key, value);
            });
            
            return axios.post(currentFlow?.ui?.action || '', formData, {
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/x-www-form-urlencoded'
                },
                withCredentials: true
            });
        },
        onSuccess: (response) => {
            console.log('=== MUTATION SUCCESS ===');
            console.log('Form submitted successfully:', response);
            console.log('Response status:', response.status);
            console.log('Response data:', response.data);
            console.log('========================');

            if (response.data.continue_with?.length > 0) {
                const continueWith = response.data.continue_with[0];
                if (continueWith.action === 'show_verification_ui' && continueWith.flow?.url) {
                    window.location.href = continueWith.flow.url;
                    return;
                }
            }
            
            if (response.data) {
                console.log('Updating currentFlow from success response');
                console.log('Previous flow state:', currentFlow?.state);
                console.log('New flow state:', response.data.state);
                setCurrentFlow(response.data);
                // Only notify parent of significant state changes
                if (response.data.state !== currentFlow?.state) {
                    onFlowUpdate?.(response.data);
                }
            }

            if (onSuccess) {
                onSuccess(response);
            }
        },
        onError: (error) => {
            console.log('=== MUTATION ERROR ===');
            console.error('Form submission failed:', error);
            console.log('Error response status:', error?.response?.status);
            console.log('Error response data:', error?.response?.data);
            console.log('======================');

            // Type assertion for axios error
            const axiosError = error;
            if (axiosError?.response?.data?.error?.id) {
                switch (axiosError.response.data.error.id) {
                case 'browser_location_change_required':
                    window.location.href = axiosError.response.data.redirect_browser_to;
                    break;
                case 'session_already_available':
                    window.location.href = '/';
                    break;
                default:
                    // Handle other error cases
                    break;
                }
            } else if (axiosError?.response?.data) {
                console.log('Updating currentFlow from error response');
                console.log('Previous flow:', currentFlow);
                console.log('New flow:', axiosError.response.data);
                console.log('Are they the same object?', currentFlow === axiosError.response.data);
                console.log('Previous flow ID:', currentFlow?.id);
                console.log('New flow ID:', axiosError.response.data.id);
                
                setCurrentFlow(axiosError.response.data);
                console.log('setCurrentFlow called');
                
                // Only notify parent of significant state changes
                if (axiosError.response.data.state !== currentFlow?.state) {
                    onFlowUpdate?.(axiosError.response.data);
                }
            }
            if (onError) onError(error);
        }
    });

    // Watch for currentFlow state changes
    useEffect(() => {
        console.log('=== CURRENT FLOW STATE CHANGED ===');
        console.log('New currentFlow:', currentFlow?.id);
        console.log('New currentFlow state:', currentFlow?.state);
        console.log('UI messages count:', currentFlow?.ui?.messages?.length || 0);
        console.log('UI nodes count:', currentFlow?.ui?.nodes?.length || 0);
        console.log('==================================');
    }, [currentFlow]);

    // Update currentFlow when flow prop changes (only when flow prop changes, not currentFlow)
    useEffect(() => {
        console.log('[KratosFlowUIForm] Flow prop changed:', flow);
        if (flow) {
            setCurrentFlow(flow);
        }
    }, [flow]);


    // Now we can safely return early if no UI
    if (!currentFlow?.ui) {
        console.log('[KratosFlowUIForm] No UI available');
        return null;
    }


    // Update groupNodes to filter out ignored groups
    const groupNodes = () => {
        const groups = currentFlow.ui.nodes.reduce((groups, node) => {
            const group = node.group || 'default';
            // Skip nodes in ignored groups
            if (ignoreUIGroups.includes(group)) {
                return groups;
            }
            if (!groups[group]) {
                groups[group] = [];
            }
            groups[group].push(node);
            return groups;
        }, {});
        // console.log('[KratosFlowUIForm] Grouped nodes:', groups);
        return groups;
    };

    const groupedNodes = groupNodes();
    // console.log('[KratosFlowUIForm] Final grouped nodes:', groupedNodes);

    // Render the shared inputs (from the 'default' group)
    const renderDefaultInputs = () => {
        const defaultNodes = groupedNodes['default'] || [];
        return defaultNodes.map((node, index) => renderNode(node, `default_${index}`));
    };

    // Render the messages for an input
    const renderMessages = (nodeMessages) => {
        return nodeMessages.map((message) => {
            // Check for message override - can be string or JSX
            const messageContent = messages[message.id] || message.text;
            
            switch (message.type) {
            case 'error':
                return (
                    <FormHelperText 
                        key={message.id} 
                        error
                        sx={{ 
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            mt: 1,
                            mb: 1
                        }}
                    >
                        {messageContent}
                    </FormHelperText>
                );
            case 'info':
                return (
                    <Typography 
                        key={message.id} 
                        variant='body1' 
                        color='primary'
                        sx={{ 
                            fontWeight: 500,
                            mt: 1,
                            mb: 1
                        }}
                    >
                        {messageContent}
                    </Typography>
                );
            default:
                return null;
            }
        });
    };

    const renderNode = (node, index) => {
        const { attributes, meta } = node;
        // Only get label for visible input fields
        const shouldShowLabel = node.type === 'input' && !['hidden', 'submit'].includes(attributes.type);
        const label = shouldShowLabel ? (meta?.label?.text || attributes?.name) : null;
        const uniqueKey = `${attributes?.name || attributes?.id}_${index}`;
        
        // Debug logging for error messages
        // if (node.messages && node.messages.length > 0) {
        //     console.log(`Node ${attributes.name} has messages:`, node.messages);
        // }

        // Determine the type of element to render based on node.type
        switch (node.type) {
            case 'input':
                // For input elements, use attributes.type to determine specific input type
                switch (attributes.type) {
                    case 'hidden':
                        return (
                            <input 
                                key={uniqueKey} 
                                type="hidden" 
                                name={attributes.name}
                                value={formData[attributes.name] || attributes.value || ''}
                                onChange={() => {}} // Hidden inputs don't need onChange
                            />
                        );
                    case 'submit':
                        return (
                            <Button
                                key={uniqueKey}
                                variant="contained"
                                type="submit"
                                disabled={attributes.disabled || mutation.isLoading}
                                value={attributes.value}
                                name={attributes.name}
                                onClick={(event) => handleFormSubmit(event, attributes.name, attributes.value)}
                                sx={{
                                    mt: 3,
                                    mb: 4,
                                    backgroundColor: '#8F2AB0',
                                    color: '#ffff',
                                    '&:hover': {
                                        backgroundColor: '#7B1FA2'
                                    }
                                }}
                                fullWidth
                            >
                                {meta?.label?.text || attributes.value}
                            </Button>
                        );
                    case 'text':
                    case 'email':
                    case 'password':
                        return (
                            <Box key={uniqueKey} mb={2}>
                                <TextField
                                    name={attributes.name}
                                    label={label}
                                    type={attributes.type}
                                    value={formData[attributes.name] || ''}
                                    onChange={(e) => handleInputChange(attributes.name, e.target.value)}
                                    disabled={attributes.disabled}
                                    autoComplete={attributes.autocomplete}
                                    fullWidth
                                    variant="outlined"
                                    margin="normal"
                                    error={!!(node.messages && node.messages.some((msg) => msg.type === 'error'))}
                                />
                                {node.messages && renderMessages(node.messages)}
                            </Box>
                        );
                    default:
                        // console.log('Unhandled input type:', attributes.type, node);
                        return null;
                }

            case 'a':
                return (
                    <a
                        key={uniqueKey}
                        href={attributes.href}
                        id={attributes.id}
                        className="inline-block px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                    >
                        {attributes.title?.text || meta?.label?.text}
                    </a>
                );

            default:
                // console.log('Unhandled node type:', node.type, node);
                return null;
        }
    };

    const renderGroupForm = (group, nodes) => {
        return (
            <form
                key={group}
                onSubmit={(e) => e.preventDefault()} // Prevent default form submission, we handle it with button clicks
            >
                {/* Render shared default inputs */}
                {renderDefaultInputs()}

                {/* Render inputs specific to this group */}
                {nodes.map((node, index) => renderNode(node, `${group}_${index}`))}

            </form>
        );
    };

    return (
        <>
            {console.log('=== COMPONENT RENDER ===', {
                flowId: currentFlow?.id,
                flowState: currentFlow?.state,
                groupedNodes: Object.keys(groupedNodes),
                globalMessages: currentFlow?.ui?.messages?.length || 0,
                formData: formData
            })}
            {currentFlow.ui.messages && currentFlow.ui.messages.map((message) => (
                <Box 
                    key={message.id} 
                    sx={{ 
                        p: 2, 
                        mb: 2, 
                        borderRadius: 1,
                        backgroundColor: message.type === 'error' ? '#ffebee' : '#e3f2fd',
                        border: message.type === 'error' ? '1px solid #ffcdd2' : '1px solid #bbdefb'
                    }}
                >
                    <Typography 
                        variant='body1' 
                        color={message.type === 'error' ? 'error' : 'info'}
                        sx={{ 
                            fontWeight: 500,
                            fontSize: '0.95rem'
                        }}
                    >
                        {messages[message.id] || message.text}
                    </Typography>
                </Box>
            ))}

            {Object.entries(groupedNodes).map(([group, nodes]) => {
                // console.log(`[KratosFlowUIForm] Rendering group: ${group} with nodes:`, nodes);
                return (
                    group !== 'default' && (
                        <Box key={group} mb={3}>
                            {!hideAllGroupTitles && !hideGroupTitles.includes(group) && (
                                <Typography variant='h6' gutterBottom>
                                    {group.charAt(0).toUpperCase() + group.slice(1)}
                                </Typography>
                            )}
                            {renderGroupForm(group, nodes)}
                        </Box>
                    )
                );
            })}

            {/* Flow JSON Output */}
            {/* <Box mt={4} mb={2}>
                <Typography variant='h6' gutterBottom>
                    Flow JSON Output
                </Typography>
                <Box 
                    sx={{
                        backgroundColor: '#f5f5f5',
                        border: '1px solid #ddd',
                        borderRadius: 1,
                        p: 2,
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        whiteSpace: 'pre-wrap'
                    }}
                >
                    {JSON.stringify(currentFlow, null, 2)}
                </Box>
            </Box> */}
        </>
    );
};

KratosFlowUIForm.propTypes = {
    flow: PropTypes.oneOfType([
        PropTypes.object,  // full flow object
        PropTypes.shape({  // just UI
            action: PropTypes.string,
            method: PropTypes.string,
            nodes: PropTypes.array
        })
    ]),
    onFlowStateChange: PropTypes.func,  // existing callback
    onFlowUpdate: PropTypes.func,       // new callback
    ignoreUIGroups: PropTypes.arrayOf(PropTypes.string), // Add prop type for ignoreUIGroups
    hideGroupTitles: PropTypes.arrayOf(PropTypes.string), // Add prop type
    hideAllGroupTitles: PropTypes.bool, // Add prop type
    messages: PropTypes.objectOf(PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.element,
        PropTypes.node
    ])),
};

export default KratosFlowUIForm;
